import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all students
 */
export const fetchStudents = createAsyncThunk(
  'students/fetchStudents',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Students.app_students_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch students');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch students');
    }
  }
);

/**
 * Fetch a single student by ID
 */
export const fetchStudentById = createAsyncThunk(
  'students/fetchStudentById',
  async ({ token, studentId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Students.app_students_get,
        studentId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch student');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch student');
    }
  }
);

/**
 * Create a new student
 */
export const createStudent = createAsyncThunk(
  'students/createStudent',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'present') {
          data.append('present', value ? 1 : 0);
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Students.app_students_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create student');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create student';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing student
 */
export const updateStudent = createAsyncThunk(
  'students/updateStudent',
  async ({ token, studentId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Only append changed fields
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        // Handle file inputs differently
        if (currentValue instanceof File) {
          data.append(key, currentValue);
        }
        // Handle present toggle
        else if (key === 'present') {
          if (currentValue !== initialValue) {
            data.append('present', currentValue ? 1 : 0);
          }
        }
        // Handle other changed fields
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Students.app_students_update,
        studentId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update student');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update student';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a student
 */
export const deleteStudent = createAsyncThunk(
  'students/deleteStudent',
  async ({ token, studentId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Students.app_students_delete,
        studentId
      );
      if (response.data.status === 200) {
        return studentId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete student');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete student');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const studentSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch students list
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch student by ID
    builder
      .addCase(fetchStudentById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create student
    builder
      .addCase(createStudent.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createStudent.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create student';
        state.formErrors = errorData.errors || {};
      });

    // Update student
    builder
      .addCase(updateStudent.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update student';
        state.formErrors = errorData.errors || {};
      });

    // Delete student
    builder
      .addCase(deleteStudent.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(student => student.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = studentSlice.actions;

// Selectors
export const selectStudents = (state) => state.students.entities;
export const selectStudentsPagination = (state) => state.students.pagination;
export const selectSelectedStudent = (state) => state.students.selectedEntity;
export const selectInitialFormData = (state) => state.students.initialFormData;
export const selectStudentsListStatus = (state) => state.students.listStatus;
export const selectStudentDetailStatus = (state) => state.students.detailStatus;
export const selectStudentCreateStatus = (state) => state.students.createStatus;
export const selectStudentUpdateStatus = (state) => state.students.updateStatus;
export const selectStudentDeleteStatus = (state) => state.students.deleteStatus;
export const selectStudentsListError = (state) => state.students.listError;
export const selectStudentDetailError = (state) => state.students.detailError;
export const selectStudentCreateError = (state) => state.students.createError;
export const selectStudentUpdateError = (state) => state.students.updateError;
export const selectStudentDeleteError = (state) => state.students.deleteError;
export const selectStudentFormErrors = (state) => state.students.formErrors;

export default studentSlice.reducer;
